import React, { useState } from 'react';
import { eventApi } from '@/services/eventsApi';
import type { Event, PaginationInfo } from '@/types/event';
import EventCard from './EventCard';
import Pagination from './Pagination';
import ReservationModal from '@/components/events/modal/ReservationModal';
import SuccessModal from '@/components/events/modal/SuccessModal';

interface EventsListProps {
  events: Event[];
  quota?: any;
  onRegister: (event: Event) => void;
  onEventClick: (event: Event) => void;
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  loading: boolean;
  registrationStatus: Record<string, boolean>;
  onRegistrationChange?: (eventId: string, isRegistered: boolean) => void;
}

const EventsList = ({ 
  events,
  quota: myQuotas = {},
  onRegister, 
  onEventClick,
  pagination, 
  onPageChange,
  loading,
  registrationStatus,
  onRegistrationChange
}: EventsListProps) => {  
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [reservationData, setReservationData] = useState<any>(null);
  const [registeringEvents, setRegisteringEvents] = useState<Set<string>>(new Set());

  const handleReserve = (event: Event) => {  
    onRegister(event);
  };

  const handleConfirmReservation = async (nextId: string|number|null = null) => {
    onRegister(selectedEvent as Event);
  };

  const handleQuickRegister = async (eventId: string | number) => {
    if (registeringEvents.has(eventId.toString())) return;

    setRegisteringEvents(prev => new Set(prev).add(eventId.toString()));
    
    try {
      // Update registration status
      if (onRegistrationChange) {
        onRegistrationChange(eventId.toString(), true);
      }
      
      // Show success feedback (you can add a toast notification here)
      console.log('Successfully registered for event');
      
    } catch (error) {
      console.error('Failed to register for event:', error);
      // Show error feedback
    } finally {
      setRegisteringEvents(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId.toString());
        return newSet;
      });
    }
  };

  const handleQuickUnregister = async (eventId: string | number) => {
    try {
      // Note: You might need to get the registration ID first
      // For now, this is a placeholder - implement based on your API
      const userRegistrations = await eventApi.getUserRegistrations();
      const registration = userRegistrations.registrations.find(reg => reg.event_id === eventId.toString());
      
      if (registration) {
        await eventApi.cancelRegistration(registration.id);
        
        // Update registration status
        if (onRegistrationChange) {
          onRegistrationChange(eventId.toString(), false);
        }
        
        console.log('Successfully unregistered from event');
      }
    } catch (error) {
      console.error('Failed to unregister from event:', error);
    }
  };

  if (loading) {
    return (
      <section className="py-4 px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="text-left mb-8 md:mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold">Upcoming Events</h2>
          <p className="text-gray-400 mt-2 text-sm sm:text-base">
            Discover professional learning opportunities
          </p>
        </div>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4 px-4 sm:px-6 md:px-8 lg:px-12">
      <div className="text-left mb-8 md:mb-10">
        <h2 className="text-2xl sm:text-3xl font-bold">Upcoming Events</h2>
        <p className="text-gray-400 mt-2 text-sm sm:text-base">
          Discover professional learning opportunities
        </p>
        <p className="text-gray-400 text-xs sm:text-sm mt-1">
          Showing {pagination.from}-{pagination.to} of {pagination.total} events
        </p>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-12 px-4">
          <p className="text-gray-400 text-base sm:text-lg">
            No events found matching your criteria.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4 sm:space-y-6 mx-auto max-w-7xl">
            {events.map((event) => (
              <EventCard 
                key={event.id} 
                event={event} 
                onViewDetails={onEventClick}
                onReserve={handleReserve}
                onClick={() => onEventClick(event)}
                onQuickRegister={handleQuickRegister}
                onQuickUnregister={handleQuickUnregister}
                isRegistered={registrationStatus[event.id.toString()] || false}
                isRegistering={registeringEvents.has(event.id.toString())}
              />
            ))}
          </div>

          <div className="mt-8 md:mt-10">
            <Pagination 
              pagination={pagination}
              onPageChange={onPageChange}
            />
          </div>
        </>
      )}
      
      {showReservationModal && selectedEvent && (
        <ReservationModal
          event={selectedEvent}
          quota={myQuotas}
          onConfirm={handleConfirmReservation}
          onClose={() => setShowReservationModal(false)}
        />
      )}

      {showSuccessModal && selectedEvent && (
        <SuccessModal
          event={selectedEvent}
          quota={myQuotas}
          reservationData={reservationData}
          onClose={() => setShowSuccessModal(false)}
        />
      )}
    </section>
  );
};

export default EventsList;